import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { team, values } from "@/data/team";
import { Sparkles, Award, Users, Lightbulb, Trophy, Clapperboard, Globe2, Film } from "lucide-react";
import { Link } from "react-router-dom";
import { HighlightBrand } from "@/components/HighlightBrand";

const iconMap = { Sparkles, Award, Users, Lightbulb };

const About = () => {
  const { t, lang } = useLang();

  const lead = team.find((m) => m.lead) || team[0];
  const rest = team.filter((m) => !m.lead);

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative pt-40 pb-24 md:pt-52 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img src="https://images.unsplash.com/photo-1782216368766-92d02eb11ce5" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/70 via-[#0A0A0C]/80 to-[#0A0A0C]" />
        </div>
        <div className="relative container-x max-w-5xl">
          <div className="overline mb-6">{t("about.eyebrow")}</div>
          <h1 className="display-h1">{t("about.title")}</h1>
          <p className="mt-8 text-white/70 text-xl leading-relaxed max-w-3xl"><HighlightBrand text={t("about.lead")} /></p>
        </div>
      </section>

      {/* STORY — long-form narrative for depth (SEO + AdSense) */}
      <section className="pt-4 pb-24 md:pt-8 md:pb-32" data-testid="about-story-section">
        <div className="container-x max-w-4xl">
          <div className="overline mb-4">{t("about.storyEyebrow")}</div>
          <h2 className="display-h2 mb-10">{t("about.storyTitle")}</h2>

          {/* Milestones strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14" data-testid="milestones-strip">
            {[
              { Icon: Clapperboard, num: "25+", label: t("about.milestoneYears") },
              { Icon: Film, num: "500+", label: t("about.milestoneProjects") },
              { Icon: Trophy, num: "15+", label: t("about.milestoneAwards") },
              { Icon: Globe2, num: "8+", label: t("about.milestoneCountries") },
            ].map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-xl border border-white/10 bg-[#0d0d10] p-5 md:p-6 text-center"
              >
                <m.Icon className="w-6 h-6 text-[#E6A95B] mx-auto mb-3" />
                <div className="font-display text-3xl md:text-4xl text-white">{m.num}</div>
                <div className="text-white/50 text-xs md:text-sm mt-1">{m.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Narrative paragraphs */}
          <article className="space-y-7 text-white/75 text-lg leading-[2.05]" data-testid="about-story-body">
            {(t("about.story") || []).map((paragraph, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
              >
                <HighlightBrand text={paragraph} />
              </motion.p>
            ))}
          </article>
        </div>
      </section>

      {/* ACHIEVEMENTS — cited honors for E-A-T signal */}
      <section className="py-24 md:py-32 bg-[#0d0d10] border-y border-white/5" data-testid="achievements-section">
        <div className="container-x max-w-5xl">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-4">{t("about.achievementsEyebrow")}</div>
            <h2 className="display-h2">{t("about.achievementsTitle")}</h2>
            <p className="mt-6 text-white/60 text-lg leading-relaxed">{t("about.achievementsIntro")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {(t("about.achievements") || []).map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="p-7 rounded-xl border border-white/10 bg-black/30 hover:border-[#E6A95B]/40 transition"
                data-testid={`achievement-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-full bg-[#E6A95B]/15 text-[#E6A95B] grid place-items-center">
                    <Trophy className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl text-white mb-2">{a.title}</h3>
                    <p className="text-white/65 text-sm md:text-base leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/awards" className="btn-ghost inline-flex items-center gap-2" data-testid="see-all-awards">
              {t("nav.awards")} →
            </Link>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="py-24 md:py-32">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-10">
          {[
            { title: t("about.missionTitle"), text: t("about.missionText") },
            { title: t("about.visionTitle"), text: t("about.visionText") },
          ].map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.15 }}
              className="glass-card rounded-xl p-10 md:p-14"
              data-testid={`about-block-${i}`}
            >
              <div className="text-[#E6A95B] font-display text-5xl">0{i + 1}</div>
              <h2 className="display-h3 mt-6">{b.title}</h2>
              <p className="mt-6 text-white/70 text-lg leading-relaxed">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TEAM — asymmetric layout: lead on the right (large), rest stacked on the left (small) */}
      <section className="py-24 md:py-32 bg-[#0d0d10] border-y border-white/5" data-testid="team-section">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div className="max-w-xl">
              <div className="overline mb-4">{t("about.teamEyebrow")}</div>
              <h2 className="display-h2">{t("about.teamTitle")}</h2>
            </div>
            <p className="max-w-md text-white/60 text-lg">{t("about.teamSub")}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* LEFT: 5 members + placeholder in a 2×3 grid on lg */}
            <div className="lg:col-span-5 order-2 grid grid-cols-2 gap-4 md:gap-5 content-center">
              {rest.map((m, i) => (
                <motion.div
                  key={m.name.en + i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="group flex flex-col items-center text-center"
                  data-testid={`team-member-${i + 1}`}
                >
                  <div className="w-full aspect-[3/4] overflow-hidden rounded-2xl border border-white/10 relative bg-[#0d0d10]">
                    {m.placeholder ? (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-3 border-2 border-dashed border-[#E6A95B]/30">
                        <div className="w-12 h-12 rounded-full border-2 border-[#E6A95B]/40 grid place-items-center text-[#E6A95B] text-2xl">+</div>
                        <span className="text-[#E6A95B] text-xs uppercase tracking-widest">{lang === "ar" ? "قريباً" : "Coming Soon"}</span>
                      </div>
                    ) : (
                      <>
                        <img
                          src={m.image}
                          alt={m.name[lang]}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[1000ms] group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                      </>
                    )}
                  </div>
                  <h4 className="font-display text-base md:text-lg leading-tight mt-3">{m.name[lang]}</h4>
                  <p className="text-white/50 text-xs md:text-sm mt-1">{m.role[lang]}</p>
                </motion.div>
              ))}
            </div>

            {/* RIGHT (in RTL, this appears on the right of the screen — large lead portrait) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7 order-1 group"
              data-testid="team-lead"
            >
              <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden rounded-2xl border border-white/10">
                <img
                  src={lead.image}
                  alt={lead.name[lang]}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[1200ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/30 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6 md:p-10">
                  <div className="overline mb-3 text-[#E6A95B]/90">{lead.role[lang]}</div>
                  <h3 className="font-display text-3xl md:text-5xl leading-tight">{lead.name[lang]}</h3>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32">
        <div className="container-x">
          <div className="max-w-2xl mb-14">
            <div className="overline mb-4">{t("about.valuesEyebrow")}</div>
            <h2 className="display-h2">{t("about.valuesTitle")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => {
              const Icon = iconMap[v.icon] || Sparkles;
              return (
                <div key={i} className="p-8 rounded-xl border border-white/10 bg-[#0d0d10] card-hover" data-testid={`value-${i}`}>
                  <Icon className="w-6 h-6 text-[#E6A95B]" />
                  <h4 className="mt-6 font-display text-xl">{v.title[lang]}</h4>
                  <p className="mt-3 text-white/55 text-sm leading-relaxed">{v.desc[lang]}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA with cinematic studio background */}
      <section className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/bg/studio.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/85 via-[#0A0A0C]/75 to-[#0A0A0C]" />
        </div>
        <div className="relative container-x text-center max-w-3xl mx-auto">
          <h2 className="display-h2 italic">{t("contact.title")}</h2>
          <div className="mt-8">
            <Link to="/contact" className="btn-primary" data-testid="about-cta">{t("nav.cta")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
